import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Check, X, Clock, AlertTriangle } from "lucide-react";
import { CollapseToggle } from "./CollapseToggle";
import { useCollapsed } from "../hooks/useCollapsed";
import { useAuth } from "../auth/AuthContext";

/**
 * Phase 49c — 7-day Live Trades history across S5 / S6 / S8.
 * Shows: Date · Section · Symbol · Setup · Dir · RVOL · % Chg · Phase ·
 * P&L · Close reason · Order ID.
 * Collapsible (Show/Hide) via the standard `useCollapsed` pattern.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 2026-08-28 — see the matching constant in LiveOrderAuditLog.jsx. This
// table's "Action" column only ever rendered a static phase pill; there was
// no way to close a still-open live position from this cross-section view.
const EXIT_ENDPOINT = {
  section5: "/candle-breakout/manual-exit",
  section6: "/oi-autopilot/manual-exit",
  section8: "/vwap-bounce/manual-exit",
  momentum_oi: "/momentum-oi/paper-trades/manual-exit",
};

const fmt = (v, d = 2) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-IN", {
        maximumFractionDigits: d,
        minimumFractionDigits: d,
      });

const fmtIST = (iso) => {
  if (!iso) return "—";
  try {
    let s = String(iso);
    if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = s + "Z";
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    })
      .format(new Date(s))
      .replace(",", "");
  } catch {
    return "—";
  }
};

const PHASE_STYLE = {
  FILLED: { color: "var(--bull)", icon: Check, label: "Filled" },
  OPEN: { color: "#FFB66E", icon: Clock, label: "Open" },
  REJECTED: { color: "var(--bear)", icon: X, label: "Rejected" },
  SOLD: { color: "var(--bull)", icon: Check, label: "Sold" },
  CLOSED: { color: "var(--text-muted)", icon: Check, label: "Closed" },
  UNKNOWN: { color: "var(--text-muted)", icon: AlertTriangle, label: "Unknown" },
};

const PhasePill = ({ phase }) => {
  const cfg = PHASE_STYLE[phase] || PHASE_STYLE.UNKNOWN;
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-mono-data px-2 py-0.5 border"
      style={{ color: cfg.color, borderColor: cfg.color }}
    >
      <Icon size={11} /> {cfg.label}
    </span>
  );
};

const SECTION_LABEL = {
  section5: "S5 · Breakout",
  section6: "S6 · OI Long",
  section8: "S8 · VWAP",
  momentum_oi: "Momentum OI",
};

const Pill = ({ children, color }) => (
  <span
    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-mono-data px-1.5 py-0.5 border"
    style={{ color, borderColor: color }}
  >
    {children}
  </span>
);

// 2026-09-13 — extracted from LiveOrdersHistory's render so an admin viewer
// can render one of these per user group (chat: "Admin should be able to
// see for all users (one table per user)"), while a regular user (whose
// `data.orders` the backend now already scopes to just their own rows)
// still gets exactly the single table this always was.
const HistoryTable = ({ orders, exitingId, onManualExit, days }) => {
  const grouped = useMemo(() => {
    const out = new Map();
    for (const o of orders || []) {
      const day = (o.entered_at || "").slice(0, 10) || (o.day || "—");
      if (!out.has(day)) out.set(day, []);
      out.get(day).push(o);
    }
    return Array.from(out.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [orders]);

  if (!orders?.length) {
    return (
      <div className="px-4 py-6 text-xs text-muted-3 text-center" data-testid="live-orders-history-empty">
        No live trades in the last {days} days.
      </div>
    );
  }

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-grid text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
          <th className="text-left px-4 py-2">Entry IST</th>
          <th className="text-left px-4 py-2">Exit IST</th>
          <th className="text-left px-4 py-2">User</th>
          <th className="text-left px-4 py-2">Section</th>
          <th className="text-left px-4 py-2">Symbol</th>
          <th className="text-left px-4 py-2">Setup · Dir</th>
          <th className="text-right px-4 py-2">Strike</th>
          <th className="text-right px-4 py-2" title="Number of lots placed at entry (monthly-cycle sizing)">Lots</th>
          <th className="text-right px-4 py-2" title="Relative Volume = latest 5m vol / avg-10">RVOL</th>
          <th className="text-right px-4 py-2" title="Intraday % change of underlying">% Chg</th>
          <th className="text-right px-4 py-2">Buy ₹</th>
          <th className="text-right px-4 py-2">Exit ₹</th>
          <th className="text-right px-4 py-2">P&amp;L</th>
          <th className="text-left px-4 py-2">Action</th>
          <th className="text-left px-4 py-2">Close Reason</th>
        </tr>
      </thead>
      <tbody>
        {grouped.map(([day, rows]) =>
          rows.map((o, i) => {
            const pnl = o.final_pnl_pct ?? o.pnl_pct;
            const pnlColor =
              pnl == null
                ? "var(--text-muted)"
                : pnl >= 0
                ? "var(--bull)"
                : "var(--bear)";
            const isShort = (o.direction || "LONG").toUpperCase() === "SHORT";
            return (
              <tr
                key={`${o.id || `${day}-${i}`}`}
                className="border-b border-grid hover:bg-white/[0.02]"
                data-testid={`live-orders-history-row-${day}-${i}`}
              >
                <td className="px-4 py-2 text-muted-2 font-mono-data text-[10px]">
                  {fmtIST(o.entered_at)}
                </td>
                <td className="px-4 py-2 text-muted-2 font-mono-data text-[10px]">
                  {o.closed_at ? fmtIST(o.closed_at) : <span className="text-muted-3">—</span>}
                </td>
                <td className="px-4 py-2 font-mono-data text-[10px]">
                  {o.user_name || <span className="text-muted-3">—</span>}
                </td>
                <td className="px-4 py-2 font-mono-data text-[10px] text-muted-1">
                  {SECTION_LABEL[o.section] || o.section}
                </td>
                <td className="px-4 py-2 font-semibold">{o.symbol}</td>
                <td className="px-4 py-2">
                  {o.setup_type || o.direction ? (
                    <Pill
                      color={
                        isShort ? "var(--bear)" : "var(--bull)"
                      }
                    >
                      {o.setup_type ? `${o.setup_type} · ` : ""}
                      {isShort ? "SHORT" : "LONG"}
                    </Pill>
                  ) : (
                    <span className="text-muted-3">—</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right font-mono-data">
                  {o.atm_strike ? (
                    `${o.atm_strike} ${isShort ? "PE" : "CE"}`
                  ) : (
                    <span className="text-muted-3">—</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right font-mono-data text-muted-2">
                  {o.lots ?? <span className="text-muted-3">—</span>}
                </td>
                <td
                  className="px-4 py-2 text-right font-mono-data"
                  data-testid={`live-orders-history-rvol-${day}-${i}`}
                  style={{
                    color:
                      (o.rvol ?? 0) >= 1.5
                        ? "var(--bull)"
                        : "var(--text-muted)",
                  }}
                >
                  {o.rvol != null ? `${fmt(o.rvol, 2)}×` : <span className="text-muted-3">—</span>}
                </td>
                <td
                  className="px-4 py-2 text-right font-mono-data"
                  style={{
                    color:
                      o.change_pct == null
                        ? "var(--text-muted)"
                        : o.change_pct >= 0
                        ? "var(--bull)"
                        : "var(--bear)",
                  }}
                >
                  {o.change_pct == null
                    ? <span className="text-muted-3">—</span>
                    : `${o.change_pct >= 0 ? "+" : ""}${fmt(o.change_pct, 2)}%`}
                </td>
                <td className="px-4 py-2 text-right font-mono-data">
                  {o.live_avg_fill_price
                    ? `₹${fmt(o.live_avg_fill_price)}`
                    : o.live_buy_price
                    ? `₹${fmt(o.live_buy_price)}`
                    : <span className="text-muted-3">—</span>}
                </td>
                <td className="px-4 py-2 text-right font-mono-data">
                  {o.live_sell_price ?? o.close_ce_ltp ? (
                    <>
                      {`₹${fmt(o.live_sell_price ?? o.close_ce_ltp)}`}
                      {!o.live_sell_price && o.close_ce_ltp && (
                        <span className="text-muted-3 text-[9px] ml-1" title="Exit detected via broker sync, not placed by the app">
                          (broker)
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-muted-3">—</span>
                  )}
                </td>
                <td
                  className="px-4 py-2 text-right font-mono-data text-[10px]"
                  style={{ color: pnlColor }}
                  data-testid={`live-orders-history-pnl-${day}-${i}`}
                >
                  {pnl == null
                    ? <span className="text-muted-3">—</span>
                    : `${pnl >= 0 ? "+" : ""}${fmt(pnl, 2)}%`}
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-1.5">
                    <PhasePill phase={o.phase} />
                    {o.status === "OPEN" && EXIT_ENDPOINT[o.section] && (
                      <button
                        data-testid={`live-orders-history-exit-${day}-${i}`}
                        onClick={() => onManualExit(o)}
                        disabled={exitingId === o.id}
                        title={o.live_trade ? "Manual Exit — validates position then places SELL on Upstox" : "Manual Exit — paper close at current LTP"}
                        className="text-[10px] uppercase tracking-wider font-mono-data px-1.5 py-0.5 border hover:bg-white/5 disabled:opacity-50"
                        style={{ color: "var(--bear)", borderColor: "var(--bear)" }}
                      >
                        {exitingId === o.id ? "…" : "Exit"}
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2 text-muted-2 font-mono-data text-[10px]">
                  {o.close_reason || <span className="text-muted-3">—</span>}
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
};

export const LiveOrdersHistory = () => {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);
  const [collapsed, toggle] = useCollapsed("live-orders-history", true);
  const [exitingId, setExitingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/live-orders/audit?days=${days}`);
      setData(r.data);
    } catch {
      toast.error("Failed to load 7-day live orders history");
    } finally {
      setLoading(false);
    }
  }, [days]);

  // Same pattern as CandleBreakout.jsx's onManualExit, routed to the right
  // section's existing endpoint from this cross-section view.
  const onManualExit = async (o) => {
    const endpoint = EXIT_ENDPOINT[o.section];
    if (!endpoint) {
      toast.error(`No manual-exit endpoint for section "${o.section}"`);
      return;
    }
    if (!window.confirm(
      `Manually exit ${o.symbol}?\n\nThis will close the trade at the latest LTP. ` +
      `Live trades will trigger a SELL on Upstox (only if a position still exists).`
    )) return;
    setExitingId(o.id);
    try {
      const r = await axios.post(`${API}${endpoint}`, {
        trade_id: o.id,
        action: "MANUAL_EXIT",
      });
      const trade = r?.data?.trade;
      const pnl = trade?.final_pnl_pct;
      const skipped = trade?.sell_skipped_reason === "no_position";
      toast.success(
        `${o.symbol}: Manual exit${pnl != null ? ` · P&L ${pnl >= 0 ? "+" : ""}${pnl}%` : ""}` +
        (skipped ? " · sell skipped (no Upstox position)" : "")
      );
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Manual exit failed";
      toast.error(`${o.symbol}: ${detail}`);
    } finally {
      setExitingId(null);
    }
  };

  useEffect(() => {
    if (!collapsed) load();
  }, [collapsed, load]);

  return (
    <section className="space-y-3" data-testid="live-orders-history-section">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h3 className="text-sm uppercase tracking-[0.18em] font-mono-data text-muted-1">
            Live Trades — Last {days} Days
          </h3>
          {data?.counts?.total != null && !collapsed && (
            <span className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
              · {data.counts.total} orders
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!collapsed && (
            <>
              <select
                data-testid="live-orders-history-days"
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="border border-grid surface px-2 py-1 text-[10px] uppercase tracking-wider font-mono-data hover:bg-white/5"
              >
                <option value={1}>1 day</option>
                <option value={3}>3 days</option>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
              </select>
              <button
                onClick={load}
                disabled={loading}
                data-testid="live-orders-history-refresh"
                className="border-grid surface px-3 py-1 text-[10px] uppercase tracking-wider font-mono-data hover:bg-white/5 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
                Refresh
              </button>
            </>
          )}
          <CollapseToggle
            collapsed={collapsed}
            onToggle={toggle}
            testId="live-orders-history-toggle"
          />
        </div>
      </div>

      {!collapsed && (
        loading && !data ? (
          <div className="border-grid surface overflow-x-auto px-4 py-6 text-xs text-muted-3 text-center" data-testid="live-orders-history-body">
            Loading {days}-day history…
          </div>
        ) : isAdmin && data?.by_user ? (
          <div className="space-y-4" data-testid="live-orders-history-body">
            {data.by_user.length === 0 ? (
              <div className="border-grid surface px-4 py-6 text-xs text-muted-3 text-center" data-testid="live-orders-history-empty">
                No live trades in the last {days} days.
              </div>
            ) : (
              data.by_user.map((grp) => (
                <div key={grp.user_id || "unknown"} className="border-grid surface overflow-x-auto" data-testid={`live-orders-history-user-group-${grp.user_id || "unknown"}`}>
                  <div className="px-4 py-2 border-b border-grid text-[11px] uppercase tracking-wider font-mono-data text-muted-1 flex items-center gap-2">
                    {grp.user_name}
                    <span className="text-muted-3 normal-case tracking-normal">· {grp.counts?.total ?? grp.orders.length} order{grp.orders.length === 1 ? "" : "s"}</span>
                  </div>
                  <HistoryTable orders={grp.orders} exitingId={exitingId} onManualExit={onManualExit} days={days} />
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="border-grid surface overflow-x-auto" data-testid="live-orders-history-body">
            <HistoryTable orders={data?.orders || []} exitingId={exitingId} onManualExit={onManualExit} days={days} />
          </div>
        )
      )}
    </section>
  );
};